@ -0,0 +1,100 @@
<template>
  <div ref="containerEl" class="handshake-leaderboard">
    <div
      class="row"
      v-for="username in truncatedLeaders"
      :key="`player-${username}`"
      :class="{ 'is-lead': isLeading(username) }"
    >
      <RouterLink
        :to="{ name: RouterNameType.USERNAME, params: { username }, query: { back: 'history' } }"
        class="username"
      >
        @{{ username }} <span class="you" v-if="username === userDetails?.username">You</span>
      </RouterLink>
      <div class="score">
        <GamIcon :name="GamIconName.HANDSHAKE" :is-primary="isLeading(username)" />
        {{ rankings[username] }}
      </div>
    </div>

    <GamButton
      class="show-more"
      v-if="isTruncated && leaders.length > truncatedLeaders.length"
      @click="showMore"
      :size="GamButtonSize.SMALL"
      :variant="GamButtonVariant.STEKLO"
    >
      Show more
    </GamButton>
  </div>
</template>

<script setup lang="ts">
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import { useAuthStore } from '@/stores/auth.store';
import GamButton from '@/views/components/GamButton.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';

export interface HandshakeBoardProps {
  handshakes: HandshakeDto[];
  isTruncated?: boolean;
  length?: number;
}

const props = defineProps<HandshakeBoardProps>();

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const containerEl = ref<HTMLDivElement>();

type Username = string;
type Score = number;

export interface HandshakeLeaderboard {
  [key: Username]: Score;
}

const rankings = computed(() =>
  props.handshakes.reduce((rankings: HandshakeLeaderboard, handshake: HandshakeDto) => {
    if (!handshake.isConfirmed || (!handshake.initiator && !handshake.responder)) {
      return rankings;
    }

    const initiator = handshake.initiator?.username;
    if (initiator) {
      rankings[initiator] = (rankings[initiator] || 0) + 1;
    }

    const responder = handshake.responder?.username;
    if (responder) {
      rankings[responder] = (rankings[responder] || 0) + 1;
    }

    return rankings;
  }, {} as HandshakeLeaderboard),
);

const leaders = computed(() =>
  Object.keys(rankings.value).sort((a: Username, b: Username) => (rankings.value[b] || 0) - (rankings.value[a] || 0)),
);

/** The count at the top of the stand — everyone who shares it stands in colour. */
const leadingScore = computed(() => Math.max(0, ...Object.values(rankings.value)));

const isLeading = (username: Username): boolean =>
  leadingScore.value > 0 && rankings.value[username] === leadingScore.value;

const length = ref<number>(props.length || 5);

const truncatedLeaders = computed(() => {
  if (props.isTruncated) {
    return leaders.value.slice(0, length.value);
  }

  return leaders.value;
});

const showMore = () => {
  length.value = length.value * 2;
};

const scrollIntoView = () => {
  containerEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

defineExpose({ scrollIntoView });
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.row {
  display: grid;
  grid-template-columns: 1fr auto;
}

.row:nth-child(even) {
  background: linear-gradient(to right, transparent, var(--color-white-3), transparent);
}

.username,
.score {
  line-height: 1.25rem;
  padding: 0.75rem 0;
  transform: translateY(-0.5px);
  color: var(--color-white-100);
}

.username {
  font-weight: 700;
  letter-spacing: 0.03em;
  font-size: 1rem;
  text-decoration: none;
  padding-left: 0.25em;
}

.row.is-lead .username,
.row.is-lead .score,
.row.is-lead .you {
  color: var(--color-pink);
}

@include hover-capable {
  .username:hover {
    color: var(--color-pink);

    .you {
      color: var(--color-white-30);
    }
  }

  .row.is-lead .username:hover {
    color: var(--color-white-100);

    .you {
      color: var(--color-white-30);
    }
  }
}

.you {
  font-weight: 300;
  color: var(--color-white-30);
}

.score {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-l);
  font-weight: 500;
  font-size: 1rem;
  padding-right: 0.5em;
}

.show-more {
  margin-top: var(--spacing-md);
}
</style>
